/**
 * GroupNode
 *
 * A transparent container node that wraps the entire Identity Proof section.
 * ReactFlow renders all child nodes (identity method nodes, selfie, liveness
 * decisions, and their terminals) inside this group boundary.
 *
 * Its size (width, height), border, and background are NOT set here — they are
 * computed dynamically in utils.js based on how many identity methods are enabled
 * and whether liveness check is active, then passed via the node's `style` prop.
 *
 * Position in flowchart:
 *   [HeaderNode]
 *        ↓
 *   ┌─────────────────── GroupNode ───────────────────┐
 *   │  [GovtID]  [KBA]  [Bank]                        │
 *   │                [Selfie]                          │
 *   │         [FaceMatch?]   [Liveness?]               │
 *   └─────────────────────────────────────────────────┘
 *        ↓
 *   [DecisionNode (Identity Proof Valid?)]
 *
 * Handles:
 *   - target-top:    receives the edge coming down from HeaderNode
 *   - source-bottom: connects downward to the Identity Proof decision node
 *
 * Data props:
 *   - label (optional): section heading shown at the top of the group
 *                       (e.g. "IDENTITY PROOF")
 */
import React from 'react';

import { Box } from '@mui/material';
import { Handle, Position } from '@xyflow/react';
import PropTypes from 'prop-types';

import styles from '../../FlowChartCanvas.module.css';

function GroupNode({ data }) {
    return (
        <>
            {/* Optional section heading rendered at the top center of the group */}
            {data?.label && <Box className={styles.groupNodeHeading}>{data.label}</Box>}

            {/* Receives the edge coming down from HeaderNode */}
            <Handle type="target" position={Position.Top} id="target-top" style={{ opacity: 0 }} />

            {/* Connects downward to the Identity Proof decision node */}
            <Handle
                type="source"
                position={Position.Bottom}
                id="source-bottom"
                style={{ opacity: 0 }}
            />
        </>
    );
}

GroupNode.propTypes = {
    data: PropTypes.shape({
        label: PropTypes.string,
    }).isRequired,
};

export default GroupNode;
